import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import WrButton from 'ember-wiser/components/button';
import type { medsInterface, ProcessedMedications } from '../form';
import type { TreatmentFormChangeset } from '../treatment-form-modal';
import Dropdown from './dropdown';

interface Args {
  changeset: TreatmentFormChangeset;
  prefix: (property: string) => string;
  medicationsGroupedByAtc: Map<string, ProcessedMedications[]>;
  currentMedication: medsInterface;
  isCurrentMedicationStopped: boolean;
}

export default class MedicationColumnsComponent extends Component<Args> {
  /**
   * When true, we display the dropdown
   * When false, we display 4 columns
   */
  @tracked isEditing = this.args.changeset[this.args.prefix('isNew')] ?? false;

  get medicationStyles() {
    const styles = ['align-top'];

    if (this.args.isCurrentMedicationStopped) styles.push('opacity-[0.45]');

    return styles.join(' ');
  }

  onChange = async () => {
    this.isEditing = false;
    await this.args.changeset.validate();
  };

  <template>
    {{#if this.isEditing}}
      <td colspan='4' class='align-top'>
        <Dropdown
          @changeset={{@changeset}}
          @propertyName={{@prefix 'medication'}}
          @allowAdditions={{true}}
          @onChange={{this.onChange}}
          @fullTextSearch='exact'
          as |dropdown|
        >
          {{#each-in @medicationsGroupedByAtc as |_atc medications|}}

            {{#each medications as |medication|}}
              <dropdown.Item
                @key={{medication.key}}
                @value={{medication.value}}
              />
            {{/each}}
            <dropdown.Divider />
          {{/each-in}}
        </Dropdown>
      </td>
      <td></td>
    {{else}}
      {{#if @currentMedication.name}}
        <td
          colspan='4'
          class='{{this.medicationStyles}} font-bold'
          data-test-medication-column='name'
        >{{@currentMedication.name}}</td>
      {{else}}
        <td
          class='{{this.medicationStyles}} font-bold'
          data-test-medication-column='brand'
        >{{@currentMedication.brand}}</td>
        <td
          class={{this.medicationStyles}}
          data-test-medication-column='moleculeLabel'
        >{{@currentMedication.moleculeLabel}}</td>
        <td class={{this.medicationStyles}} data-test-medication-column='dose'>
          {{@currentMedication.dose}}
          {{@currentMedication.unit}}
        </td>
        <td
          class={{concat this.medicationStyles ' break-all'}}
          data-test-medication-column='administration'
        >
          {{@currentMedication.administrationRoute}}
        </td>
      {{/if}}
      <td class='align-top'><WrButton
          @icon='Pen'
          @variant='link'
          {{on 'click' (fn (mut this.isEditing) true)}}
          data-test-medication-column='edit-button'
        /></td>
    {{/if}}
  </template>
}
